// Keys to commands: one pure step.
//
// `step` takes the parser's state, one input (a key, time passing, or the
// person turning away), the bindings table and what is live (the sections
// of the active scopes, innermost first), and gives the next state and the
// commands the keys named. It reads no clock and keeps no timer: time
// arrives as an input like any key, so the whole grammar can be tested as
// a table of inputs and results.
//
// The grammar it reads: an optional count (`3`), then a key sequence from
// the table (`] c`, `g o`, `r`). The count is carried in the command.

import { counted, withCount, type Command } from '../commands/command';
import { continuations, hits, leadsOn, takesCounts, type Compiled, type Continuation, type Section } from './bindings';
import { keyLabel, plain, type Chord } from './chord';

/** How long, in milliseconds, a count or the start of a sequence waits for its next key. */
export const PENDING_MS = 1000;

/** The largest count: more digits are not read into it. */
export const MAX_COUNT = 9999;

export type ParserState =
  | { kind: 'idle' }
  /** Count digits typed, waiting for more or for the sequence they multiply. */
  | { kind: 'count'; digits: string; at: number }
  /** The start of a sequence, waiting for its next key; `count` is what was typed before it. */
  | { kind: 'prefix'; count: number | null; keys: readonly string[]; at: number }
  /** What was typed named nothing and was refused at `at`: the keys right after it are part of the same mistake. */
  | { kind: 'rejected'; at: number };

export const IDLE: ParserState = { kind: 'idle' };

export type Input =
  | { kind: 'key'; chord: Chord; at: number }
  /** Time passing: `at` is the time now. A count or prefix older than `PENDING_MS` lapses. */
  | { kind: 'elapsed'; at: number }
  /** The person turned to something else (a click, the window losing the focus): nothing stays pending. */
  | { kind: 'reset' };

/** A scope whose rows are live, by the section of the table it names. */
export interface LiveSection {
  section: Section;
  /** A text field's own scope: its rows are live while the field is typed in. */
  field: boolean;
}

export interface Context {
  /** The live sections, innermost first. */
  sections: readonly LiveSection[];
  /** The key is typed into a text field: only rows marked for that, and a field's own, are live. */
  typing: boolean;
  /** The focus is on a control that uses Space and Enter itself. */
  onControl: boolean;
}

/** A command the keys named, and the section whose row named it. */
export interface Match {
  section: Section;
  command: Command;
  /** The keys, as written for a person, when the row is a deprecated alias. */
  deprecated?: string;
}

/**
 * What was typed and refused: a sequence no live row completes
 * (`no-such-key`), or a count before `command`, which takes none
 * (`no-count`). Nothing ran.
 */
export interface Rejected {
  count: number | null;
  keys: readonly string[];
  why: 'no-such-key' | 'no-count';
  command?: Command;
}

/** What is typed so far and what may follow, for showing while the parser waits. */
export interface Pending {
  count: number | null;
  keys: readonly string[];
  next: readonly Continuation[];
}

export interface Stepped {
  state: ParserState;
  /** The commands the input completed, the innermost section's first: the first one a scope carries out is the one that ran. */
  matches: readonly Match[];
  /** The parser used the key: its default action is to be suppressed, whether or not it named a command. */
  taken: boolean;
  pending: Pending | null;
  /** Set when this input was refused, to be said to the person. */
  rejected?: Rejected;
}

const none = (state: ParserState, taken: boolean): Stepped => ({ state, matches: [], taken, pending: null });

const chordOfKey = (key: string): Chord => ({ key, ctrl: false, meta: false });

/** The state at `at`: idle once a count, a prefix or a rejection has stood `PENDING_MS`. */
function live(state: ParserState, at: number): ParserState {
  return state.kind !== 'idle' && at - state.at >= PENDING_MS ? IDLE : state;
}

/**
 * One step of the parser.
 *
 * - A digit starts or extends a count (`0` only extends one) wherever a
 *   live section has something to count; the count multiplies the command
 *   that follows, where a count means anything to it.
 * - A key that completes a row runs it; one that starts longer rows waits
 *   for the next key. A key that completes nothing after a count or a
 *   prefix is rejected: the wait ends, nothing runs, and what was typed
 *   is reported (`rejected`). While the rejection stands (`PENDING_MS`
 *   from it, not extended by what is typed in it) a key that would run a
 *   command by itself is swallowed: a count comes before its motion, so
 *   `] 3 c` is one mistake, and its `c` must not act as a key of its own.
 *   A prefix key or a digit starts a new sequence as from idle, so
 *   retyping at once works; a key nothing binds keeps its default.
 * - A count before a command that takes none is rejected the same way:
 *   the command does not run once in its place. Where a key names a
 *   counted command and an uncounted one, the counted takes the count.
 * - Esc ends a wait or a rejection and does nothing else.
 * - A Ctrl or Meta combination ends a wait and is read as itself; so is a
 *   key typed into a text field, where only rows marked `inText` and the
 *   field's own are live.
 * - A row marked `yields` is not live while the focus is on a control.
 */
export function step(state: ParserState, input: Input, bindings: Compiled, ctx: Context): Stepped {
  if (input.kind === 'reset') return none(IDLE, false);
  if (input.kind === 'elapsed') return withPending(live(state, input.at), bindings, ctx, false);

  const { chord, at } = input;
  const now = live(state, at);
  const matchesOf = (typed: readonly Chord[], count: number | null, rows: (s: LiveSection, inText: boolean) => boolean): Match[] =>
    ctx.sections.flatMap((s) =>
      hits(bindings, s.section, typed)
        .filter((h) => rows(s, !!h.leaf.row.inText) && !(h.leaf.row.yields && ctx.onControl))
        .map((h) => ({ section: s.section, command: withCount(h.command, count), ...(h.leaf.row.alias === 'deprecated' ? { deprecated: typed.map((c) => keyLabel(c.key)).join('') } : {}) })),
    );

  if (ctx.typing) {
    const matches = matchesOf([chord], null, (s, inText) => s.field || inText);
    return { state: IDLE, matches, taken: matches.length > 0, pending: null };
  }
  if (!plain(chord)) {
    const matches = matchesOf([chord], null, () => true);
    return { state: IDLE, matches, taken: matches.length > 0, pending: null };
  }
  if (now.kind !== 'idle' && chord.key === 'Escape') return none(IDLE, true);
  if (now.kind === 'rejected') {
    // What the key would do typed afresh decides what becomes of it while the rejection stands.
    const fresh = step(IDLE, input, bindings, ctx);
    // The start of a sequence or a count: the person is typing it again, and it is read as from idle.
    if (fresh.state.kind !== 'idle') return fresh;
    // A key that would run a command by itself is the mistake's stray tail (the c of ] 3 c): swallowed. A key
    // nothing binds is left alone, with its default (Tab, Enter on a button); neither ends the rejection.
    return none(now, fresh.matches.length > 0);
  }

  const sections = ctx.sections.map((s) => s.section);
  if (now.kind !== 'prefix' && /^[0-9]$/.test(chord.key) && (now.kind === 'count' || chord.key !== '0') && sections.some((s) => takesCounts(bindings, s))) {
    const digits = (now.kind === 'count' ? now.digits : '') + chord.key;
    const next: ParserState = { kind: 'count', digits: Number(digits) > MAX_COUNT ? String(MAX_COUNT) : digits, at };
    return withPending(next, bindings, ctx, true);
  }

  const count = now.kind === 'count' ? Number(now.digits) : now.kind === 'prefix' ? now.count : null;
  const keys = [...(now.kind === 'prefix' ? now.keys : []), chord.key];
  const typed = keys.map(chordOfKey);
  const named = matchesOf(typed, count, () => true);
  // A count goes only to commands that take one.
  const matches = count === null ? named : named.filter((m) => counted(m.command.kind));
  if (matches.length) return { state: IDLE, matches, taken: true, pending: null };
  const refuse = (rejected: Rejected): Stepped => ({ state: { kind: 'rejected', at }, matches: [], taken: true, pending: null, rejected });
  if (named.length) return refuse({ count, keys, why: 'no-count', command: named[0].command });
  if (sections.some((s) => leadsOn(bindings, s, typed))) return withPending({ kind: 'prefix', count, keys, at }, bindings, ctx, true);
  // Nothing completes it: rejected after a count or a prefix, left alone otherwise.
  return now.kind === 'idle' ? none(IDLE, false) : refuse({ count, keys, why: 'no-such-key' });
}

function withPending(state: ParserState, bindings: Compiled, ctx: Context, taken: boolean): Stepped {
  if (state.kind === 'idle' || state.kind === 'rejected') return none(state, taken);
  const sections = ctx.sections.map((s) => s.section);
  const keys = state.kind === 'prefix' ? state.keys : [];
  const count = state.kind === 'count' ? Number(state.digits) : state.count;
  return { state, matches: [], taken, pending: { count, keys, next: keys.length ? continuations(bindings, sections, keys.map(chordOfKey)) : [] } };
}
